import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box, Grid, CardContent, Typography, Button, TextField, Checkbox,
    FormControlLabel, CircularProgress, Alert, Divider, RadioGroup,
    Radio, Stack, Avatar, Paper, IconButton, Fade, useTheme, LinearProgress
} from '@mui/material';
import useDataFetch, { usePostDataFetch } from '../../utils/useDataFetch';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import PollRoundedIcon from '@mui/icons-material/PollRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import HowToVoteRoundedIcon from '@mui/icons-material/HowToVoteRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';

const TeamFeed = () => {
    const { teamId } = useParams();
    const theme = useTheme();
    const { data, error, isLoading, refetch } = useDataFetch({ url: `/news/team/${teamId}` });
    const { postData: createPost, isLoading: isPosting } = usePostDataFetch();
    const { postData: votePoll, isLoading: isVoting } = usePostDataFetch();

    const [postType, setPostType] = useState('news');
    const [newsData, setNewsData] = useState({ titel: '', inhalt: '' });
    const [pollData, setPollData] = useState({ frage: '', optionen: ['', ''], mehrfachauswahl: false });
    const [selectedVotes, setSelectedVotes] = useState({});

    const handleCreatePost = async (e) => {
        e.preventDefault();
        if (postType === 'news') {
            await createPost({ url: `/news/team/${teamId}/post`, aBody: newsData, skipAuth: false });
            setNewsData({ titel: '', inhalt: '' });
        } else {
            await createPost({ url: `/news/team/${teamId}/poll`, aBody: pollData, skipAuth: false });
            setPollData({ frage: '', optionen: ['', ''], mehrfachauswahl: false });
        }
        refetch();
    };

    const handleVote = async (pollId) => {
        const optionIds = selectedVotes[pollId] || [];
        if (optionIds.length === 0) return;
        await votePoll({ url: `/news/poll/${pollId}/vote`, aBody: { optionIds }, skipAuth: false });
        refetch();
    };

    const toggleVote = (pollId, optionId, isMultiple) => {
        setSelectedVotes(prev => {
            const current = prev[pollId] || [];
            if (isMultiple) {
                return { ...prev, [pollId]: current.includes(optionId) ? current.filter(id => id !== optionId) : [...current, optionId] };
            } else {
                return { ...prev, [pollId]: [optionId] };
            }
        });
    };

    if (isLoading) return (
        <CircularProgress />
    );
    if (error) return <Alert severity="error" sx={{ borderRadius: "12px", mt: 4 }}>{error}</Alert>;

    const feed = data?.feed || [];

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 900, mx: 'auto', mb: 12 }}>

            {/* Header */}
            <Fade in timeout={800}>
                <Box sx={{ mb: 6 }}>
                    <Typography variant="h2" sx={{ fontWeight: 900, letterSpacing: '-0.04em' }}>
                        Team <span style={{ color: theme.palette.primary.main }}>Feed</span>
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 500, opacity: 0.8 }}>
                        Neuigkeiten und Abstimmungen für deine Mannschaft.
                    </Typography>
                </Box>
            </Fade>

            {/* Create Post Form */}
            <Paper
                sx={{
                    mb: 6,
                    borderRadius: "12px",
                    border: '1px solid',
                    borderColor: 'divider',
                    overflow: 'hidden',
                    background: 'rgba(255,255,255,0.02)',
                }}
            >
                <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', gap: 1 }}>
                    <Button
                        startIcon={<CampaignRoundedIcon />}
                        onClick={() => setPostType('news')}
                        sx={{
                            borderRadius: "12px",
                            fontWeight: 800,
                            bgcolor: postType === 'news' ? 'primary.main' : 'transparent',
                            color: postType === 'news' ? '#000' : 'text.secondary',
                            '&:hover': { bgcolor: postType === 'news' ? 'primary.dark' : 'rgba(255,255,255,0.05)' }
                        }}
                    >
                        Ankündigung
                    </Button>
                    <Button
                        startIcon={<PollRoundedIcon />}
                        onClick={() => setPostType('poll')}
                        sx={{
                            borderRadius: "12px",
                            fontWeight: 800,
                            bgcolor: postType === 'poll' ? 'primary.main' : 'transparent',
                            color: postType === 'poll' ? '#000' : 'text.secondary',
                            '&:hover': { bgcolor: postType === 'poll' ? 'primary.dark' : 'rgba(255,255,255,0.05)' }
                        }}
                    >
                        Umfrage
                    </Button>
                </Box>

                <Box sx={{ p: 3 }}>
                    <form onSubmit={handleCreatePost}>
                        {postType === 'news' ? (
                            <Stack spacing={2.5}>
                                <TextField
                                    label="Titel der Nachricht"
                                    fullWidth
                                    value={newsData.titel}
                                    onChange={e => setNewsData({ ...newsData, titel: e.target.value })}
                                    required
                                />
                                <TextField
                                    label="Was gibt es Neues?"
                                    multiline
                                    rows={3}
                                    fullWidth
                                    value={newsData.inhalt}
                                    onChange={e => setNewsData({ ...newsData, inhalt: e.target.value })}
                                    required
                                />
                            </Stack>
                        ) : (
                            <Stack spacing={2}>
                                <TextField
                                    label="Deine Frage"
                                    fullWidth
                                    value={pollData.frage}
                                    onChange={e => setPollData({ ...pollData, frage: e.target.value })}
                                    required
                                />
                                <Grid container spacing={2}>
                                    {pollData.optionen.map((opt, idx) => (
                                        <Grid  key={idx}>
                                            <TextField
                                                label={`Option ${idx + 1}`}
                                                fullWidth
                                                value={opt}
                                                onChange={e => {
                                                    const newOpts = [...pollData.optionen];
                                                    newOpts[idx] = e.target.value;
                                                    setPollData({ ...pollData, optionen: newOpts });
                                                }}
                                                required={idx < 2}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Button
                                        size="small"
                                        startIcon={<AddCircleRoundedIcon />}
                                        onClick={() => setPollData({ ...pollData, optionen: [...pollData.optionen, ''] })}
                                        sx={{ fontWeight: 800 }}
                                    >
                                        Option hinzufügen
                                    </Button>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={pollData.mehrfachauswahl}
                                                onChange={e => setPollData({ ...pollData, mehrfachauswahl: e.target.checked })}
                                            />
                                        }
                                        label={<Typography variant="body2" sx={{ fontWeight: 700 }}>Mehrfachauswahl erlauben</Typography>}
                                    />
                                </Stack>
                            </Stack>
                        )}
                        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                variant="contained"
                                type="submit"
                                disabled={isPosting}
                                startIcon={<SendRoundedIcon />}
                                sx={{ px: 4, py: 1.5, fontWeight: 900, borderRadius: "12px" }}
                            >
                                Veröffentlichen
                            </Button>
                        </Box>
                    </form>
                </Box>
            </Paper>

            {/* Feed List */}
            <Stack spacing={4}>
                {feed.map((item, idx) => (
                    <Fade in timeout={500 + (idx * 100)} key={item.id || idx}>
                        <Paper
                            sx={{
                                p: 4,
                                borderRadius: "12px",
                                border: '1px solid',
                                borderColor: 'divider',
                                background: 'rgba(255,255,255,0.01)',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                    borderColor: 'primary.main',
                                    transform: 'translateY(-4px)',
                                    bgcolor: 'rgba(190, 242, 100, 0.02)'
                                }
                            }}
                        >
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Avatar
                                        sx={{
                                            bgcolor: 'primary.main',
                                            color: '#000',
                                            fontWeight: 900,
                                            boxShadow: '0 0 15px rgba(190, 242, 100, 0.3)'
                                        }}
                                    >
                                        {item.first_name?.[0]}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                                            {item.first_name} {item.last_name}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontFamily: '"Fira Code", monospace' }}>
                                            {new Date(item.created_at).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })}
                                        </Typography>
                                    </Box>
                                </Stack>
                                <IconButton size="small"><MoreVertRoundedIcon /></IconButton>
                            </Stack>

                            {item.type === 'news' ? (
                                <Box>
                                    <Typography variant="h5" sx={{ fontWeight: 900, mb: 1.5, color: 'primary.main' }}>
                                        {item.titel}
                                    </Typography>
                                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, opacity: 0.9 }}>
                                        {item.inhalt}
                                    </Typography>
                                </Box>
                            ) : (
                                <Box>
                                    <Typography variant="h5" sx={{ fontWeight: 900, mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <PollRoundedIcon sx={{ color: 'primary.main' }} /> {item.frage}
                                    </Typography>

                                    <Stack spacing={2.5} sx={{ mb: 4 }}>
                                        {item.optionen.map(opt => {
                                            const isSelected = (selectedVotes[item.id] || []).includes(opt.id);
                                            const totalVotes = item.optionen.reduce((acc, o) => acc + (o.votes || 0), 0);
                                            const percentage = totalVotes > 0 ? (opt.votes / totalVotes) * 100 : 0;

                                            return (
                                                <Box key={opt.id}>
                                                    <Box
                                                        sx={{
                                                            p: 2,
                                                            borderRadius: "12px",
                                                            border: '1px solid',
                                                            borderColor: (opt.userVoted || isSelected) ? 'primary.main' : 'divider',
                                                            bgcolor: (opt.userVoted || isSelected) ? 'rgba(190, 242, 100, 0.05)' : 'rgba(255,255,255,0.02)',
                                                            cursor: opt.userVoted ? 'default' : 'pointer',
                                                            position: 'relative',
                                                            overflow: 'hidden',
                                                            transition: '0.2s'
                                                        }}
                                                        onClick={() => !opt.userVoted && toggleVote(item.id, opt.id, item.mehrfachauswahl)}
                                                    >
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
                                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                                {item.mehrfachauswahl ? (
                                                                    <Checkbox
                                                                        checked={opt.userVoted || isSelected}
                                                                        disabled={opt.userVoted}
                                                                        sx={{ p: 0 }}
                                                                    />
                                                                ) : (
                                                                    <Radio
                                                                        checked={opt.userVoted || isSelected}
                                                                        disabled={opt.userVoted}
                                                                        sx={{ p: 0 }}
                                                                    />
                                                                )}
                                                                <Typography sx={{ fontWeight: 800 }}>{opt.text}</Typography>
                                                            </Stack>
                                                            <Typography sx={{ fontWeight: 900, fontFamily: '"Fira Code", monospace', fontSize: '0.9rem' }}>
                                                                {opt.votes}
                                                            </Typography>
                                                        </Box>

                                                        {/* Progress indicator */}
                                                        <Box
                                                            sx={{
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                height: '100%',
                                                                width: `${percentage}%`,
                                                                bgcolor: 'primary.main',
                                                                opacity: 0.1,
                                                                transition: 'width 1s cubic-bezier(0.4, 0, 0.2, 1)'
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Stack>

                                    {!item.optionen.some(o => o.userVoted) && (
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            startIcon={<HowToVoteRoundedIcon />}
                                            onClick={() => handleVote(item.id)}
                                            disabled={isVoting || !(selectedVotes[item.id] && selectedVotes[item.id].length > 0)}
                                            sx={{ py: 1.5, fontWeight: 900, borderRadius: "12px" }}
                                        >
                                            Abstimmen
                                        </Button>
                                    )}
                                    {item.optionen.some(o => o.userVoted) && (
                                        <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 900, textTransform: 'uppercase' }}>
                                            Du hast bereits abgestimmt
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </Paper>
                    </Fade>
                ))}
                {feed.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 15, borderRadius: "12px", border: '1px dashed', borderColor: 'divider' }}>
                        <Typography variant="h6" sx={{ color: 'text.disabled', fontWeight: 700 }}>
                            Noch keine Beiträge im Feed.
                        </Typography>
                    </Box>
                )}
            </Stack>
        </Box>
    );
};

export default TeamFeed;
